/**
 * AdLens FX — Knowledge Base
 *
 * Structured requirements and recommendations for every AdTech SDK and spec
 * that the tool validates. The PROXY (`proxy-server/knowledge/*.json`) is now
 * the single source of truth — this module fetches the packs at runtime
 * (`GET /proxy/knowledge`, via api/checks.js's `getKnowledgePacks()`) and
 * caches them in memory, rather than bundling its own duplicate JSON copies.
 * No AI call needed at QA time. The "Update Knowledge" button fetches latest
 * docs, runs an AI summarisation pass, and writes back updated JSON files on
 * the proxy.
 *
 * Usage:
 *   import { loadKnowledge, getRequirement, getRecommendations, getTicketContext } from '../knowledge';
 *
 *   // Kick off the fetch once at app boot (fire-and-forget, idempotent)
 *   loadKnowledge();
 *
 *   // Look up a single requirement by ID
 *   const req = getRequirement('ima_cust_params');
 *
 *   // All requirements that apply to a request type
 *   const reqs = getRequirementsFor('ima');
 *
 *   // Build bug ticket "EXPECTED" text for a given check key
 *   const expected = getExpectedText('ima_cust_params');
 *
 * IMPORTANT — timing: before `loadKnowledge()` resolves, every helper below
 * degrades gracefully (empty arrays / null / fallback strings) rather than
 * throwing. Call-time consumers (RuleEngine.js, VideoLifecycleView.jsx) rely
 * on `loadKnowledge()` having been kicked off at app boot (see App.js) so the
 * cache is populated by the time a real validation pass runs; there is a
 * brief window right after boot where a validation could run before the
 * fetch resolves — that's an accepted, small race (see App.js comment).
 */

import { getKnowledgePacks } from '../api/checks';

/** All loaded packs. Reassigned (not mutated in place) after loadKnowledge()
 *  resolves — Babel's ESM live-binding means call-time readers like
 *  `KNOWLEDGE_BASE.filter(...)` inside functions see the populated array,
 *  even though this was empty when the importing module first evaluated. */
export let KNOWLEDGE_BASE = [];

let _packs = [];
let _reqById = {};
let _recById = {};
let _loaded = false;
let _loadingPromise = null;

function _rebuildIndexes() {
  const reqById = {};
  for (const kb of _packs) {
    for (const req of kb.requirements || []) {
      reqById[req.id] = { ...req, _source: kb.name, _sourceId: kb.id, _sourceUrl: kb.sourceUrls?.[0] };
    }
  }
  const recById = {};
  for (const kb of _packs) {
    for (const rec of kb.recommendations || []) {
      recById[rec.id] = { ...rec, _source: kb.name, _sourceId: kb.id };
    }
  }
  _reqById = reqById;
  _recById = recById;
}

/**
 * Fetch the knowledge packs from the proxy (GET /proxy/knowledge) and
 * populate the in-memory cache. Idempotent — safe to call from multiple
 * components; concurrent callers share the same in-flight request. Never
 * throws: on failure the cache is left empty (or as previously loaded) and
 * `_loaded` stays false so a later call retries.
 * @returns {Promise<void>}
 */
export function loadKnowledge() {
  if (_loaded) return Promise.resolve();
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = getKnowledgePacks()
    .then((packs) => {
      if (Array.isArray(packs) && packs.length) {
        _packs = packs;
        _rebuildIndexes();
        KNOWLEDGE_BASE = _packs;
        _loaded = true;
      }
      // Empty/failed fetch: leave _loaded=false so a later call retries.
    })
    .catch(() => {
      // getKnowledgePacks() already never throws, but be defensive anyway.
    })
    .finally(() => {
      _loadingPromise = null;
    });

  return _loadingPromise;
}

/** Whether the knowledge packs have successfully loaded at least once. */
export function knowledgeLoaded() {
  return _loaded;
}

// ─── Query helpers ────────────────────────────────────────────────────────────

/**
 * Look up a single requirement by its id.
 * @param {string} id
 * @returns {object|null}
 */
export function getRequirement(id) {
  return _reqById[id] || null;
}

/**
 * All requirements that apply to a given request type.
 * @param {string} requestType  e.g. 'ima', 'google', 'prebid'
 * @returns {object[]}
 */
export function getRequirementsFor(requestType) {
  return Object.values(_reqById).filter(req => {
    const appliesTo = req.appliesTo || req._appliesTo || [];
    // If no appliesTo on the requirement, fall back to the KB's appliesTo
    const kbApplies = _packs.find(k => k.id === req._sourceId)?.appliesTo || [];
    const scope = appliesTo.length ? appliesTo : kbApplies;
    return scope.includes(requestType);
  });
}

/**
 * The "EXPECTED" text for a given check key — used in bug ticket generation.
 * Falls back to a generic message if the key isn't in the KB.
 * @param {string} checkKey  e.g. 'ima_cust_params', 'gma_sz'
 * @returns {string}
 */
export function getExpectedText(checkKey) {
  const req = getRequirement(checkKey);
  if (req) {
    return req.description || req.failMessage || 'Check did not meet the required condition.';
  }
  // Built-in fallbacks for validation keys not in KB
  const FALLBACKS = {
    c2s_ss:         'SpringServe C2S bid must be captured before IMA stream registration',
    c2s_aps:        'APS C2S bid must be captured before IMA stream registration',
    c2s_timing:     'C2S bid requests must fire before IMA registration to allow token passthrough',
    ima_reg:        'IMA DAI stream registration POST must be captured (pubads.g.doubleclick.net)',
    ima_tokens_ss:  'SS bid token (ss_request_key / ss_ad_cache_key) must appear in IMA cust_params when SS bid received',
    ima_tokens_aps: 'APS bid token (amzn_b / amznslots) must appear in IMA cust_params when APS bid received',
    ad_filled:      'At least one VAST tracking start beacon must be captured per ad break',
    q_start:        'VAST js_start beacon must fire when ad begins playing',
    q_complete:     'VAST js_complete beacon must fire when ad reaches 100%',
    q_all:          'All five VAST quartile beacons (▶ 25% 50% 75% ✓) must fire in order',
    vast_errors:    'No VAST error beacons should fire during normal ad playback',
    ima_sdk:        'All required IMA SDK params (app_id, ad_player_name, msid, omid, cust_params) must be present',
    gma_iu:         'GAM ad unit (iu=) must be a valid /networkId/placement/slot path — single-segment IDs indicate third-party creative calls',
    gma_sz:         'Ad size (sz=) must declare at least one valid IAB size matching the slot configuration in GAM',
    gma_correlator: 'Correlator must be consistent across all ad requests in the same page session',
    gma_rdid:       'Resettable device ID (rdid= / IDFA) must be present when ATT consent is granted',
    gma_lmt:        'Limit ad tracking signal (lmt=) must accurately reflect the user\'s ATT choice',
  };
  return FALLBACKS[checkKey] || 'Check did not meet the required condition.';
}

/**
 * Reference URL for a given check key.
 * @param {string} checkKey
 * @returns {string|null}
 */
export function getReferenceUrl(checkKey) {
  return getRequirement(checkKey)?.reference || null;
}

/**
 * All recommendations across the full knowledge base, optionally filtered by KB id.
 * @param {string} [kbId]  e.g. 'ima-sdk'
 * @returns {object[]}
 */
export function getRecommendations(kbId) {
  return Object.values(_recById).filter(r => !kbId || r._sourceId === kbId);
}

/**
 * Summary of all KB entries for display in the Update Knowledge panel.
 * @returns {{ id, name, vendor, version, lastUpdated, sourceUrls }[]}
 */
export function getKBSummary() {
  return _packs.map(({ id, name, vendor, version, lastUpdated, sourceUrls }) => ({
    id, name, vendor, version, lastUpdated, sourceUrls,
    requirementCount: Object.values(_reqById).filter(r => r._sourceId === id).length,
    recommendationCount: Object.values(_recById).filter(r => r._sourceId === id).length,
  }));
}

/**
 * Build the full "EXPECTED + REFERENCE" block for a bug ticket.
 * @param {string} checkKey
 * @returns {{ expected: string, reference: string|null, source: string|null }}
 */
export function getTicketContext(checkKey) {
  const req = getRequirement(checkKey);
  return {
    expected:  getExpectedText(checkKey),
    reference: req?.reference || null,
    source:    req?._source   || null,
  };
}
